import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ClerkProvider, SignIn, SignUp } from '@clerk/clerk-react';
import { HelmetProvider } from 'react-helmet-async';
import { lazy, Suspense } from "react";
import Home from "./pages/Home";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import logger from "@/lib/logger";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { loadEngine } from "@/lib/engine";

// Route-level code splitting: each system page loads on demand
const CosmicProfile = lazy(() => import("./pages/CosmicProfile"));
const Pythagorean = lazy(() => import("./pages/Pythagorean"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ApiKeys = lazy(() => import("./pages/ApiKeys"));
const Settings = lazy(() => import("./pages/Settings"));
const Agrippa = lazy(() => import("./pages/Agrippa"));
const Enochian = lazy(() => import("./pages/Enochian"));
const Sepharial = lazy(() => import("./pages/Sepharial"));
const EliphasLevi = lazy(() => import("./pages/EliphasLevi"));
const Liber777 = lazy(() => import("./pages/Liber777"));
const Abramelin = lazy(() => import("./pages/Abramelin"));
const Plato = lazy(() => import("./pages/Plato"));
const Chaldean = lazy(() => import("./pages/Chaldean"));
const BaZi = lazy(() => import("./pages/BaZi"));
const Vedic = lazy(() => import("./pages/Vedic"));
const Western = lazy(() => import("./pages/Western"));
const Gematria = lazy(() => import("./pages/Gematria"));
const Terms = lazy(() => import("./pages/Terms"));

// Warm the browser-side OCaml engine in the background so first
// calculation is instant
loadEngine();

const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center min-h-[50vh]">
    <span className="font-mystical text-2xl animate-pulse">☉</span>
  </div>
);

// Get Clerk publishable key
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// TEMPORARILY DISABLED - Authentication disabled for testing
// if (!clerkPubKey) {
//   throw new Error('Missing Publishable Key');
// }

const queryClient = new QueryClient();

// Routes component that works with or without Clerk
function AppRoutes() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/profile" element={<CosmicProfile />} />
        <Route path="/pythagorean" element={<Pythagorean />} />
        <Route path="/chaldean" element={<Chaldean />} />
        
        {/* Divination Systems */}
        <Route path="/bazi" element={<BaZi />} />
        
        {/* Astrology Systems */}
        <Route path="/vedic" element={<Vedic />} />
        <Route path="/western" element={<Western />} />
        
        {/* Esoteric Systems */}
        <Route path="/gematria" element={<Gematria />} />
        <Route path="/agrippa" element={<Agrippa />} />
        <Route path="/enochian" element={<Enochian />} />
        <Route path="/sepharial" element={<Sepharial />} />
        <Route path="/eliphas-levi" element={<EliphasLevi />} />
        <Route path="/liber777" element={<Liber777 />} />
        <Route path="/abramelin" element={<Abramelin />} />
        <Route path="/plato" element={<Plato />} />
        
        {/* Utility pages */}
        <Route path="/faq" element={<FAQ />} />
        {/* <Route path="/pricing" element={<Pricing />} /> */}
        <Route path="/terms" element={<Terms />} />
        
        {/* Auth-protected routes - only show if Clerk is available */}
        {clerkPubKey && (
          <>
            <Route path="/api-keys" element={
              <ProtectedRoute>
                <ApiKeys />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/sign-in/*" element={<SignIn routing="path" path="/sign-in" />} />
            <Route path="/sign-up/*" element={<SignUp routing="path" path="/sign-up" />} />
          </>
        )}
        
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      <Footer />
    </div>
  );
}

// Wrapper component to use React Router hooks with Clerk
function ClerkProviderWithRoutes() {
  // If no Clerk key, just render routes without authentication
  if (!clerkPubKey) {
    return <AppRoutes />;
  }
  
  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      afterSignInUrl="/"
      afterSignUpUrl="/"
    >
      <AppRoutes />
    </ClerkProvider>
  );
}

const App = () => {
  // Log app initialization
  logger.info('App initializing');
  
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ProfileProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ClerkProviderWithRoutes />
              </BrowserRouter>
            </TooltipProvider>
          </ProfileProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;

